import { apiClient } from "./client";
import { useQuery } from "@tanstack/react-query";
import type { AdminKpiResponse } from "./types";

export interface DeliveryReportRequest {
    teamId?: string;
    campaignId?: string;
    flyerTypeId?: string;
    from?: string;
    to?: string;
    /** Only deliveries recorded by the logged in user. On by default in the report UI. */
    onlyMine?: boolean;
    groupBy?: "none" | "team" | "flyer" | "campaign" | "date";
}

export interface DeliveryReportRow {
    campaignId?: string;
    /** Empty when rows are not grouped per campaign (by team, timeline). */
    campaignName?: string;
    teamId?: string;
    flyerTypeId?: string;
    /** Present when rows are grouped by a campaign-only flyer (no global flyer type). */
    campaignFlyerId?: string;
    flyerName: string;
    deliveries: number;
    quantitySum?: number;
}

export interface DeliveryReportSummary {
    totalDeliveries: number;
    totalQuantity: number;
    uniqueCampaigns: number;
    uniqueFlyerTypes: number;
    firstDelivery?: string;
    lastDelivery?: string;
}

export interface DeliveryReportTimeline {
    date: string;
    deliveries: number;
    quantity: number;
}

export interface DeliveryReportLineItem {
    id: string;
    campaignId: string;
    addressPointId: string;
    addressLabel: string;
    deliveredAt: string;
    flyerTypeId?: string | null;
    campaignFlyerId?: string | null;
    flyerName: string;
    quantity: number;
}

export const fetchAdminKpis = async (): Promise<AdminKpiResponse> => {
    const response = await apiClient.get<AdminKpiResponse>("/reports/admin/kpis");
    return response.data;
};

export const fetchDeliveryReport = async (request?: DeliveryReportRequest): Promise<DeliveryReportRow[]> => {
    const response = await apiClient.get<DeliveryReportRow[]>("/reports/deliveries", { params: request });
    return response.data;
};

export const fetchDeliveryReportSummary = async (request?: DeliveryReportRequest): Promise<DeliveryReportSummary> => {
    const response = await apiClient.get<DeliveryReportSummary>("/reports/deliveries/summary", { params: request });
    return response.data;
};

export const fetchDeliveryReportByTeam = async (request?: DeliveryReportRequest): Promise<DeliveryReportRow[]> => {
    const response = await apiClient.get<DeliveryReportRow[]>("/reports/deliveries/by-team", { params: request });
    return response.data;
};

export const fetchDeliveryReportByFlyer = async (request?: DeliveryReportRequest): Promise<DeliveryReportRow[]> => {
    const response = await apiClient.get<DeliveryReportRow[]>("/reports/deliveries/by-flyer", { params: request });
    return response.data;
};

export const fetchDeliveryReportTimeline = async (request?: DeliveryReportRequest): Promise<DeliveryReportTimeline[]> => {
    const response = await apiClient.get<DeliveryReportTimeline[]>("/reports/deliveries/timeline", { params: request });
    return response.data;
};

export const fetchDeliveryReportLineItems = async (
    request?: DeliveryReportRequest
): Promise<DeliveryReportLineItem[]> => {
    const response = await apiClient.get<DeliveryReportLineItem[]>("/reports/deliveries/line-items", { params: request });
    return response.data;
};

export interface UpdateDeliveryFlyerTypeBody {
    flyerTypeId?: string;
    targetCampaignFlyerId?: string;
}

export const updateDeliveryFlyerType = async (
    deliveryId: string,
    body: UpdateDeliveryFlyerTypeBody
): Promise<void> => {
    await apiClient.patch(`/deliveries/${deliveryId}/flyer-type`, body);
};

export interface BulkReassignDeliveryFlyerTypeRequest {
    campaignId: string;
    teamId?: string;
    from?: string;
    to?: string;
    flyerTypeFilterId?: string;
    onlyMine?: boolean;
    sourceCampaignFlyerId?: string;
    sourceFlyerTypeId?: string;
    targetCampaignFlyerId?: string;
    targetFlyerTypeId?: string;
    targetCampaignId?: string;
}

export const bulkReassignDeliveryFlyerType = async (
    body: BulkReassignDeliveryFlyerTypeRequest
): Promise<{ affectedCount: number }> => {
    const response = await apiClient.post<{ affectedCount: number }>("/deliveries/bulk-reassign-flyer-type", body);
    return response.data;
};

export const useDeliveryReport = (request?: DeliveryReportRequest) => {
    return useQuery({
        queryKey: ["reports", "deliveries", request],
        queryFn: () => fetchDeliveryReport(request),
        staleTime: 60 * 1000,
    });
};

export const useDeliveryReportSummary = (request?: DeliveryReportRequest) => {
    return useQuery({
        queryKey: ["reports", "deliveries", "summary", request],
        queryFn: () => fetchDeliveryReportSummary(request),
        staleTime: 60 * 1000,
    });
};

export const useDeliveryReportByTeam = (request?: DeliveryReportRequest) => {
    return useQuery({
        queryKey: ["reports", "deliveries", "by-team", request],
        queryFn: () => fetchDeliveryReportByTeam(request),
        staleTime: 60 * 1000,
    });
};

export const useDeliveryReportByFlyer = (request?: DeliveryReportRequest) => {
    return useQuery({
        queryKey: ["reports", "deliveries", "by-flyer", request],
        queryFn: () => fetchDeliveryReportByFlyer(request),
        staleTime: 60 * 1000,
    });
};

export const useDeliveryReportTimeline = (request?: DeliveryReportRequest) => {
    return useQuery({
        queryKey: ["reports", "deliveries", "timeline", request],
        queryFn: () => fetchDeliveryReportTimeline(request),
        staleTime: 60 * 1000,
    });
};

export const useDeliveryReportLineItems = (request?: DeliveryReportRequest) => {
    return useQuery({
        queryKey: ["reports", "deliveries", "line-items", request],
        queryFn: () => fetchDeliveryReportLineItems(request),
        enabled: !!request?.campaignId,
        staleTime: 60 * 1000,
    });
};

// Activity Reports
export interface ActivityReportRequest {
    teamId?: string;
    campaignId?: string;
    type?: string; // CampaignActivityType
    from?: string;
    to?: string;
    userId?: string;
}

export interface ActivityReportRow {
    campaignId: string;
    teamId: string;
    type: string; // CampaignActivityType
    activities: number;
    quantitySum?: number;
}

export const fetchActivityReport = async (request?: ActivityReportRequest): Promise<ActivityReportRow[]> => {
    const response = await apiClient.get<ActivityReportRow[]>("/reports/activities", { params: request });
    return response.data;
};

export const useActivityReport = (request?: ActivityReportRequest) => {
    return useQuery({
        queryKey: ["reports", "activities", request],
        queryFn: () => fetchActivityReport(request),
        staleTime: 60 * 1000,
    });
};